import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    desc = "desc",
    link = "link",
    label = "label",
    cssClass = "cssClass",
    openInNewWidow = "openInNewWidow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cssClass]: string;
        [fields.desc]: string;
        [fields.link]: LinkFieldValue;
        [fields.label]: string;
        [fields.openInNewWidow]: boolean;
    };
}

type Props = OwnProps;

export interface SurTabularDataStyles {
    container?: InjectableCss;
    ctacontainer?: InjectableCss;
    title?: TypographyProps;
    descStyles?: TypographyProps;
    ctaLink?: LinkProps;
}
export const tabularDataStyles: SurTabularDataStyles = {
    container: {
        css: css`
            padding: 52px 0px 60px 0px;
            max-width: 955px;
            margin: 0 auto;
        `,
    },
    title: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 30px;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${getColor("text.main")};
            font-size: ${TYPOGRAPHY.size.xlargest};
            line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
        `,
    },
    descStyles: {
        variant: "p",
        css: css`
            text-align: center;
            width: 100%;
            margin: 0 auto;
            max-width: 955px;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-weight: ${TYPOGRAPHY.weight.base};
            color: ${getColor("text.main")};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
        `,
    },
    ctacontainer: {
        css: css`
            text-align: center;
            margin-top: 30px;
        `,
    },
    ctaLink: {
        css: css`
            outline: 0;
            background-color: ${getColor("text.main")};
            border: 1px solid ${getColor("primary")};
            height: auto;
            padding: 10px 34px;
            width: max-content;
            max-width: 100%;
            display: block;
            margin: 0 auto;
            span {
                color: ${getColor("text.main")} !important;
                display: block;
                text-align: center;
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: ${TYPOGRAPHY.weight.bold};
                color: ${getColor("primary.contrast")} !important;
                font-size: ${TYPOGRAPHY.size.base};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
            }
        `,
    },
};

const styles = tabularDataStyles;

const SurCustomInfoContent: React.FC<Props> = ({ fields, id }) => {
    const { url } = useGetLink(fields.link);

    return (
        <StyledWrapper {...styles.container}>
            {fields.title ? <Typography {...styles.title}>{fields.title}</Typography> : null}
            {fields.desc ? <Typography {...styles.descStyles}>{fields.desc}</Typography> : null}
            {fields.label && fields.link.value ? (
                <StyledWrapper {...styles.ctacontainer}>
                    <Link href={url} target={fields.openInNewWidow ? `_blank` : `_self`} {...styles.ctaLink}>
                        {fields.label}
                    </Link>
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

const SurCustomInfoContentWidget: WidgetModule = {
    component: SurCustomInfoContent,
    definition: {
        group: "Common",
        displayName: "Sur Custom Info Content Widget",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.desc,
                displayName: "Description",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.label,
                fieldType: "General",
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 2,
            },

            {
                name: fields.link,
                displayName: "Button Link",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
            },
            {
                name: fields.openInNewWidow,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default SurCustomInfoContentWidget;
